import { useEffect, useState, FormEvent, ChangeEvent } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import { formatInstant } from '@/lib/dates';
import { apiGet, apiSend } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { PageHeader, Card, Field, ErrorNote, EmptyState, inputClass } from '@/components/ui';
import BaLawExport from '@/components/BaLawExport';
import MeterReadings from '@/components/MeterReadings';
import { SOUL_AFFILIATIONS, SoulAffiliationCode } from '@/lib/flightTypes';

interface Passkey { id: string; name: string | null; created_at: string; last_used_at: string | null }
interface Pilot {
  id: string; name: string; email?: string | null;
  initials?: string | null; affiliation: SoulAffiliationCode; is_owner: boolean;
}

export default function Account() {
  const { user } = useAuth();
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pkBusy, setPkBusy] = useState(false);

  const [pilots, setPilots] = useState<Pilot[]>([]);
  const [newPilot, setNewPilot] = useState({ name: '', initials: '', affiliation: 'hired_pilot' as SoulAffiliationCode });

  function loadKeys() {
    apiGet<{ passkeys: Passkey[] }>('/api/account/passkeys').then((d) => setPasskeys(d.passkeys || [])).catch(() => {});
  }
  useEffect(loadKeys, []);

  function loadPilots() {
    apiGet<{ pilots: Pilot[] }>('/api/pilots').then((d) => setPilots(d.pilots || [])).catch(() => {});
  }
  useEffect(loadPilots, []);

  async function savePilot(id: string, patch: Partial<Pilot>) {
    setError(''); setMsg('');
    try {
      await apiSend(`/api/pilots/${id}`, 'PATCH', patch);
      setMsg('Pilot updated.');
      loadPilots();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not save that pilot'); }
  }

  async function addPilot(e: FormEvent) {
    e.preventDefault(); setError(''); setMsg('');
    if (!newPilot.name.trim()) return setError('A pilot needs a name.');
    try {
      await apiSend('/api/pilots', 'POST', {
        name: newPilot.name.trim(),
        initials: newPilot.initials.trim() || undefined,
        affiliation: newPilot.affiliation,
      });
      setNewPilot({ name: '', initials: '', affiliation: 'hired_pilot' });
      setMsg('Pilot added.');
      loadPilots();
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not add that pilot'); }
  }

  async function changePassword(e: FormEvent) {
    e.preventDefault(); setError(''); setMsg('');
    if (next.length < 8) return setError('New password must be at least 8 characters.');
    if (next !== confirm) return setError('New passwords do not match.');
    setPwBusy(true);
    try {
      await apiSend('/api/account/password', 'POST', { current_password: cur, new_password: next });
      setMsg('Password updated.'); setCur(''); setNext(''); setConfirm('');
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not change password'); }
    finally { setPwBusy(false); }
  }

  async function addPasskey() {
    setError(''); setMsg(''); setPkBusy(true);
    try {
      const optionsJSON = await apiSend('/api/account/passkey/register/options', 'POST');
      const att = await startRegistration({ optionsJSON });
      await apiSend('/api/account/passkey/register/verify', 'POST', { response: att, name: deviceName() });
      setMsg('Passkey added.'); loadKeys();
    } catch (err) {
      const m = err instanceof Error ? err.message : 'Could not add passkey';
      setError(/abort|NotAllowed/i.test(m) ? 'Passkey setup was cancelled.' : m);
    } finally { setPkBusy(false); }
  }

  const [importing, setImporting] = useState(false);
  const [emailing, setEmailing] = useState(false);
  async function emailBackup() {
    setError(''); setMsg(''); setEmailing(true);
    try {
      const res = await apiSend<{ to: string }>('/api/export/email', 'POST');
      setMsg(`Backup emailed to ${res.to}.`);
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not send backup email'); }
    finally { setEmailing(false); }
  }
  async function onImport(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!window.confirm(`Import "${file.name}"? This adds its records to your current data.`)) return;
    setError(''); setMsg(''); setImporting(true);
    try {
      const data = JSON.parse(await file.text());
      const res = await apiSend<{
        flights: number; other_flights: number; roster: number;
        skipped?: { flights: number; other_flights: number; roster: number };
      }>('/api/import', 'POST', data);
      const alreadyHere = (res.skipped?.flights ?? 0) + (res.skipped?.other_flights ?? 0) + (res.skipped?.roster ?? 0);
      // Importing is idempotent, so a re-run legitimately adds nothing — say that
      // rather than reporting three zeroes as if the file were empty.
      setMsg(
        `Imported ${res.flights} flights, ${res.other_flights} co-owner flights, ${res.roster} roster entries.` +
          (alreadyHere > 0 ? ` ${alreadyHere} rows were already here and were left alone.` : '')
      );
    } catch (err) { setError(err instanceof Error ? err.message : 'Import failed'); }
    finally { setImporting(false); }
  }

  async function removePasskey(id: string) {
    try { await apiSend(`/api/account/passkeys/${id}`, 'DELETE'); setPasskeys((p) => p.filter((x) => x.id !== id)); }
    catch (err) { setError(err instanceof Error ? err.message : 'Could not remove passkey'); }
  }

  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-6 lg:p-8">
      <PageHeader title="Account" subtitle={user?.email} />

      {error && <div className="mb-4"><ErrorNote message={error} /></div>}
      {msg && <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">{msg}</div>}

      {/* The sheet BA Law want, and the PDF that mirrors it. */}
      <BaLawExport />

      {/* Pilots — who can be put on a manifest */}
      <Card className="mb-6 p-5 sm:p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">Pilots</h2>
        <p className="mt-1 text-xs text-slate-500">
          Everyone who can be picked as "Flown by". BA Law identifies each soul on their sheet by
          INITIALS — theirs already names the owner as <span className="ident">RJS</span>, so make these match.
        </p>

        <div className="mt-4 space-y-3">
          {pilots.map((p) => (
            <div key={p.id} className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
              <div className="grid gap-2 sm:grid-cols-[2fr_1fr_2fr_auto]">
                <input aria-label={`Name for ${p.name}`} defaultValue={p.name}
                  onBlur={(e) => e.target.value.trim() && e.target.value !== p.name && savePilot(p.id, { name: e.target.value.trim() })}
                  className={inputClass} />
                <input aria-label={`Initials for ${p.name}`} defaultValue={p.initials || ''} maxLength={10}
                  onBlur={(e) => e.target.value.trim().toUpperCase() !== (p.initials || '') && savePilot(p.id, { initials: e.target.value.trim().toUpperCase() })}
                  className={`${inputClass} ident`} placeholder="RJS" />
                <select aria-label={`What ${p.name} is to the operation`} value={p.affiliation}
                  onChange={(e) => savePilot(p.id, { affiliation: e.target.value as SoulAffiliationCode })}
                  className={inputClass}>
                  {SOUL_AFFILIATIONS.map((a) => (
                    <option key={a.code} value={a.code} className="bg-night-800">
                      {a.label}{a.excluded ? ' — not counted' : ''}
                    </option>
                  ))}
                </select>
                <span className="self-center text-xs text-slate-500">{p.is_owner ? 'owner' : ''}</span>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={addPilot} className="mt-4 border-t border-white/5 pt-4">
          <p className="mb-2 text-xs text-slate-500">Add a pilot — the co-owner, an instructor, a hired ferry pilot. No email needed.</p>
          <div className="grid gap-2 sm:grid-cols-[2fr_1fr_2fr_auto]">
            <input placeholder="Full name" aria-label="New pilot name" value={newPilot.name}
              onChange={(e) => setNewPilot((s) => ({ ...s, name: e.target.value }))} className={inputClass} />
            <input placeholder="Initials" aria-label="New pilot initials" value={newPilot.initials}
              onChange={(e) => setNewPilot((s) => ({ ...s, initials: e.target.value.toUpperCase() }))} className={`${inputClass} ident`} />
            <select aria-label="What they are to the operation" value={newPilot.affiliation}
              onChange={(e) => setNewPilot((s) => ({ ...s, affiliation: e.target.value as SoulAffiliationCode }))} className={inputClass}>
              {SOUL_AFFILIATIONS.map((a) => (
                <option key={a.code} value={a.code} className="bg-night-800">
                  {a.label}{a.excluded ? ' — not counted' : ''}
                </option>
              ))}
            </select>
            <button type="submit" className="btn-ghost text-sm">Add</button>
          </div>
        </form>
      </Card>

      <MeterReadings />

      {/* Backup & restore */}
      <Card className="mb-6 p-5 sm:p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">Backup &amp; restore</h2>
        <p className="mt-1 text-xs text-slate-500">A complete backup (JSON) you can re-import later. The weekly email carries this alongside BA Law&rsquo;s spreadsheet and the landscape PDF.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <a href="/api/export/json" className="btn-primary text-sm">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" /></svg>
            Backup (JSON)
          </a>
          <label className="btn-ghost cursor-pointer text-sm">
            {importing ? 'Importing…' : 'Import backup'}
            <input type="file" accept="application/json,.json" onChange={onImport} className="hidden" disabled={importing} />
          </label>
          <button onClick={emailBackup} disabled title="Disabled on this instance" className="btn-ghost text-sm opacity-50 cursor-not-allowed">
            Email me a backup (disabled)
          </button>
        </div>
        <p className="mt-3 text-xs text-slate-500">A backup is also emailed automatically every Sunday.</p>
      </Card>

      {/* Passkeys */}
      <Card className="mb-6 p-5 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">Passkeys</h2>
            <p className="text-xs text-slate-500">Sign in with Face ID / Touch ID / a security key. Password stays as a fallback.</p>
          </div>
          <button onClick={addPasskey} disabled={pkBusy} className="btn-primary text-sm">{pkBusy ? 'Adding…' : 'Add passkey'}</button>
        </div>
        <div className="mt-4">
          {passkeys.length === 0 ? (
            <EmptyState title="No passkeys yet" hint="Add one to sign in without a password." />
          ) : (
            <ul className="divide-y divide-white/5">
              {passkeys.map((k) => (
                <li key={k.id} className="-mx-2 flex items-center justify-between rounded-lg px-2 py-3 transition hover:bg-white/[0.03]">
                  <div>
                    <p className="text-sm text-white">{k.name || 'Passkey'}</p>
                    <p className="text-xs text-slate-500">
                      Added {formatInstant(k.created_at)}
                      {k.last_used_at ? ` · last used ${formatInstant(k.last_used_at)}` : ''}
                    </p>
                  </div>
                  <button onClick={() => removePasskey(k.id)} className="text-slate-500 hover:text-red-300" aria-label="Remove passkey">✕</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      {/* Change password */}
      <Card className="p-5 sm:p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-300">Change password</h2>
        <form onSubmit={changePassword} className="space-y-4">
          <Field label="Current password"><input type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} className={inputClass} /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="New password"><input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} className={inputClass} /></Field>
            <Field label="Confirm new password"><input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputClass} /></Field>
          </div>
          <button type="submit" disabled={pwBusy} className="btn-primary">{pwBusy ? 'Saving…' : 'Update password'}</button>
        </form>
      </Card>
    </div>
  );
}

function deviceName() {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return 'iOS device';
  if (/Android/.test(ua)) return 'Android device';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  return 'Passkey';
}
